'use client'

import { type ReadonlyURLSearchParams, useSearchParams } from 'next/navigation'
import {
  createContext,
  type Dispatch,
  type FC,
  type PropsWithChildren,
  useContext,
  useMemo,
  useReducer,
} from 'react'

export type RatingRange = {
  ratingMax: number | null
  ratingMin: number | null
}

type ProductSearchRefinementStateType = {
  currentCategory: string | null
  currentRating: RatingRange
  isMobile?: boolean
  openedSelectId: string
}

type Action =
  | { payload: string; type: 'closeOtherFilters' }
  | { payload?: string; type: 'resetAllFilters' }

export const RATING_DEFAULT_VALUES = {
  ratingMax: 5,
  ratingMin: 1,
}

const parseRating = (param: string | null): number | null => {
  const value = Math.trunc(Number(param ?? ''))

  return Number.isNaN(value) ? null : value
}

const getCurrentRating = (
  searchParams: ReadonlyURLSearchParams,
): RatingRange => {
  const ratingMin = parseRating(searchParams.get('ratingMin'))
  const ratingMax = Math.min(
    parseRating(searchParams.get('ratingMax')) ??
      RATING_DEFAULT_VALUES.ratingMax,
    RATING_DEFAULT_VALUES.ratingMax,
  )

  return { ratingMax, ratingMin }
}

const productSearchRefinementReducer = (
  state: ProductSearchRefinementStateType,
  action: Action,
): ProductSearchRefinementStateType => {
  const { payload, type } = action

  switch (type) {
    case 'closeOtherFilters': {
      return { ...state, openedSelectId: payload }
    }
    case 'resetAllFilters': {
      return {
        ...state,
        currentCategory: '',
        currentRating: RATING_DEFAULT_VALUES,
      }
    }
    default: {
      return state
    }
    // No default
  }
}

type ProductSearchRefinementStateContextValue = {
  dispatch: Dispatch<Action>
  state: ProductSearchRefinementStateType
}

const ProductSearchRefinementStateContext = createContext<
  ProductSearchRefinementStateContextValue | undefined
>(undefined)

export const ProductSearchRefinementStateProvider: FC<
  PropsWithChildren<{
    isMobile?: boolean
  }>
> = ({ children, isMobile }) => {
  const searchParams = useSearchParams()

  const category = searchParams.get('category')
  const currentRating = getCurrentRating(searchParams)

  const [state, dispatch] = useReducer(productSearchRefinementReducer, {
    currentCategory: category ?? '',
    currentRating,
    isMobile,
    openedSelectId: '',
  })

  const { openedSelectId } = state

  const contextValue = useMemo(
    (): {
      dispatch: Dispatch<Action>
      state: ProductSearchRefinementStateType
    } => ({
      dispatch,
      state: {
        currentCategory: category,
        currentRating,
        isMobile,
        openedSelectId,
      },
    }),
    [category, currentRating, isMobile, openedSelectId, dispatch],
  )

  return (
    <ProductSearchRefinementStateContext.Provider value={contextValue}>
      {children}
    </ProductSearchRefinementStateContext.Provider>
  )
}

export const useProductSearchRefinementStateContext = (): {
  dispatch: Dispatch<Action>
  state: ProductSearchRefinementStateType
} => {
  const contextValue = useContext(ProductSearchRefinementStateContext)

  if (contextValue === undefined) {
    throw new Error(
      'useProductSearchRefinementStateContext must be used within a ProductSearchRefinementStateProvider',
    )
  }

  return contextValue
}
